import React, { useEffect, useState } from 'react';
import { ChevronDown, Filter, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import './Medcall2DesktopClientFilter.css';

export type Medcall2ClientStatus = 'all' | 'active';

export type Medcall2ClientFilterOption = {
  label: string;
  value: string;
};

export type Medcall2DesktopClientFilterProps = {
  facilityOptions: Medcall2ClientFilterOption[];
  facilityValues: string[];
  nurseSearchText: string;
  onFacilityValuesChange: (values: string[]) => void;
  onNurseSearchTextChange: (value: string) => void;
  onProgramValuesChange: (values: string[]) => void;
  onProviderValuesChange: (values: string[]) => void;
  onStatusChange: (status: Medcall2ClientStatus) => void;
  programOptions: Medcall2ClientFilterOption[];
  programValues: string[];
  providerOptions: Medcall2ClientFilterOption[];
  providerValues: string[];
  reports?: React.ReactNode;
  status: Medcall2ClientStatus;
};

type FilterPopoverKey = 'facility' | 'nurse' | 'program' | 'provider';

const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

const readDesktopMediaQuery = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true;
  }

  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
};

const useDesktopVisibility = () => {
  const [isDesktop, setIsDesktop] = useState(readDesktopMediaQuery);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const updateVisibility = () => setIsDesktop(mediaQuery.matches);
    updateVisibility();
    mediaQuery.addEventListener('change', updateVisibility);
    return () => mediaQuery.removeEventListener('change', updateVisibility);
  }, []);

  return isDesktop;
};

const toggleSelectedValue = (values: string[], value: string, checked: boolean) => {
  if (checked) {
    return [...values, value].filter((candidate, index, allValues) => allValues.indexOf(candidate) === index);
  }

  return values.filter((candidate) => candidate !== value);
};

const SearchInput = ({
  onChange,
  placeholder,
  value,
}: {
  onChange: (value: string) => void;
  placeholder: string;
  value: string;
}) => {
  return (
    <div className="medcall2-client-filter__search-wrap">
      <Search aria-hidden="true" className="medcall2-client-filter__search-icon" />
      <Input
        className="medcall2-client-filter__search"
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        value={value}
      />
    </div>
  );
};

const CheckboxOptions = ({
  onChange,
  options,
  values,
}: {
  onChange: (values: string[]) => void;
  options: Medcall2ClientFilterOption[];
  values: string[];
}) => {
  const selectedValues = new Set(values);
  return (
    <div className="medcall2-client-filter__options">
      {options.map((option) => (
        <label className="medcall2-client-filter__option" key={option.value}>
          <Checkbox
            checked={selectedValues.has(option.value)}
            onCheckedChange={(checked) => {
              onChange(toggleSelectedValue(values, option.value, checked === true));
            }}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const PopoverFooter = ({ onApply, onReset }: { onApply: () => void; onReset: () => void }) => {
  return (
    <div className="medcall2-client-filter__footer">
      <Button
        className="medcall2-client-filter__footer-button"
        onClick={onReset}
        size="sm"
        type="button"
        variant="ghost"
      >
        Reset
      </Button>
      <Button
        className="medcall2-client-filter__footer-button"
        onClick={onApply}
        size="sm"
        type="button"
        variant="ghost"
      >
        Apply
      </Button>
    </div>
  );
};

type FilterPillProps = React.ComponentPropsWithoutRef<typeof Button> & {
  active: boolean;
};

const FilterPill = React.forwardRef<HTMLButtonElement, FilterPillProps>(({
  active,
  children,
  ...props
}, ref) => {
  return (
    <Button
      ref={ref}
      aria-pressed={active}
      className="medcall2-client-filter__pill"
      data-active={active}
      size="sm"
      type="button"
      variant="outline"
      {...props}
    >
      <ChevronDown aria-hidden="true" className="h-4 w-4 text-[#9353ff]" />
      {children}
    </Button>
  );
});
FilterPill.displayName = 'FilterPill';

export const Medcall2DesktopClientFilter = ({
  facilityOptions,
  facilityValues,
  nurseSearchText,
  onFacilityValuesChange,
  onNurseSearchTextChange,
  onProgramValuesChange,
  onProviderValuesChange,
  onStatusChange,
  programOptions,
  programValues,
  providerOptions,
  providerValues,
  reports,
  status,
}: Medcall2DesktopClientFilterProps) => {
  const isDesktop = useDesktopVisibility();
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [openFilter, setOpenFilter] = useState<FilterPopoverKey | null>(null);
  const [draftFacilityValues, setDraftFacilityValues] = useState(facilityValues);
  const [draftNurseSearchText, setDraftNurseSearchText] = useState(nurseSearchText);
  const [draftProgramValues, setDraftProgramValues] = useState(programValues);
  const [draftProviderValues, setDraftProviderValues] = useState(providerValues);
  const [facilitySearch, setFacilitySearch] = useState('');
  const [providerSearch, setProviderSearch] = useState('');

  const handlePopoverOpenChange = (filter: FilterPopoverKey, nextOpen: boolean) => {
    if (nextOpen) {
      setDraftFacilityValues([...facilityValues]);
      setDraftNurseSearchText(nurseSearchText);
      setDraftProgramValues([...programValues]);
      setDraftProviderValues([...providerValues]);
      setFacilitySearch('');
      setProviderSearch('');
      setOpenFilter(filter);
      return;
    }

    setOpenFilter((currentFilter) => currentFilter === filter ? null : currentFilter);
  };

  const closePopover = () => setOpenFilter(null);

  const filteredProviderOptions = providerOptions.filter((option) => {
    return option.value.toLowerCase().includes(providerSearch.toLowerCase());
  });
  const filteredFacilityOptions = facilityOptions.filter((option) => {
    return option.label.toLowerCase().includes(facilitySearch.toLowerCase());
  });

  if (!isDesktop) {
    return null;
  }

  return (
    <section className="medcall2-client-filter" data-testid="medcall2-desktop-client-filter">
      <div className="medcall2-client-filter__tools">
        <Button
          aria-controls="medcall2-client-filter-controls"
          aria-expanded={filtersVisible}
          className="medcall2-client-filter__toggle"
          onClick={() => setFiltersVisible((currentValue) => !currentValue)}
          type="button"
          variant="link"
        >
          <Filter aria-hidden="true" className="h-4 w-4" />
          Filters
        </Button>
        {reports ? <div className="medcall2-client-filter__reports">{reports}</div> : null}
      </div>

      {filtersVisible ? (
        <div className="medcall2-client-filter__controls" id="medcall2-client-filter-controls">
          <div className="medcall2-client-filter__status-wrap">
            <select
              aria-label="Client Status"
              className="medcall2-client-filter__status"
              onChange={(event) => {
                const nextStatus = event.target.value;
                if (nextStatus !== 'all' && nextStatus !== 'active') {
                  return;
                }

                onStatusChange(nextStatus);
              }}
              value={status}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
            </select>
            <ChevronDown aria-hidden="true" className="medcall2-client-filter__status-icon" />
          </div>

          <Popover
            onOpenChange={(nextOpen) => handlePopoverOpenChange('nurse', nextOpen)}
            open={openFilter === 'nurse'}
          >
            <PopoverTrigger asChild>
              <FilterPill active={nurseSearchText.length > 0}>Nurse Search</FilterPill>
            </PopoverTrigger>
            <PopoverContent align="end" className="medcall2-client-filter__popover">
              <SearchInput
                onChange={setDraftNurseSearchText}
                placeholder="Search Text"
                value={draftNurseSearchText}
              />
              <PopoverFooter
                onApply={() => {
                  if (draftNurseSearchText.length < 1) {
                    return;
                  }

                  onNurseSearchTextChange(draftNurseSearchText);
                  closePopover();
                }}
                onReset={() => {
                  setDraftNurseSearchText('');
                  onNurseSearchTextChange('');
                  closePopover();
                }}
              />
            </PopoverContent>
          </Popover>

          <Popover
            onOpenChange={(nextOpen) => handlePopoverOpenChange('program', nextOpen)}
            open={openFilter === 'program'}
          >
            <PopoverTrigger asChild>
              <FilterPill active={programValues.length > 0}>Program</FilterPill>
            </PopoverTrigger>
            <PopoverContent align="end" className="medcall2-client-filter__popover">
              <CheckboxOptions
                onChange={setDraftProgramValues}
                options={programOptions}
                values={draftProgramValues}
              />
              <PopoverFooter
                onApply={() => {
                  if (draftProgramValues.length < 1) {
                    return;
                  }

                  onProgramValuesChange([...draftProgramValues]);
                  closePopover();
                }}
                onReset={() => {
                  setDraftProgramValues([]);
                  onProgramValuesChange([]);
                  closePopover();
                }}
              />
            </PopoverContent>
          </Popover>

          <Popover
            onOpenChange={(nextOpen) => handlePopoverOpenChange('provider', nextOpen)}
            open={openFilter === 'provider'}
          >
            <PopoverTrigger asChild>
              <FilterPill active={providerValues.length > 0}>Provider</FilterPill>
            </PopoverTrigger>
            <PopoverContent align="end" className="medcall2-client-filter__popover">
              <SearchInput
                onChange={setProviderSearch}
                placeholder="Search Provider"
                value={providerSearch}
              />
              <CheckboxOptions
                onChange={setDraftProviderValues}
                options={filteredProviderOptions}
                values={draftProviderValues}
              />
              <PopoverFooter
                onApply={() => {
                  if (draftProviderValues.length < 1) {
                    return;
                  }

                  onProviderValuesChange([...draftProviderValues]);
                  closePopover();
                }}
                onReset={() => {
                  setDraftProviderValues([]);
                  onProviderValuesChange([]);
                  closePopover();
                }}
              />
            </PopoverContent>
          </Popover>

          <Popover
            onOpenChange={(nextOpen) => handlePopoverOpenChange('facility', nextOpen)}
            open={openFilter === 'facility'}
          >
            <PopoverTrigger asChild>
              <FilterPill active={facilityValues.length > 0}>Facility</FilterPill>
            </PopoverTrigger>
            <PopoverContent align="end" className="medcall2-client-filter__popover">
              <SearchInput
                onChange={setFacilitySearch}
                placeholder="Search Facility"
                value={facilitySearch}
              />
              <CheckboxOptions
                onChange={setDraftFacilityValues}
                options={filteredFacilityOptions}
                values={draftFacilityValues}
              />
              <PopoverFooter
                onApply={() => {
                  if (draftFacilityValues.length < 1) {
                    return;
                  }

                  onFacilityValuesChange([...draftFacilityValues]);
                  closePopover();
                }}
                onReset={() => {
                  setDraftFacilityValues([]);
                  onFacilityValuesChange([]);
                  closePopover();
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
      ) : null}
    </section>
  );
};
